// Demo Data Manager - Uses localStorage for demo mode data
// This provides the same API as Firebase managers but stores data locally

import { IPin } from '../types/Pin';
import { Album } from '../types/Album';
import { IRecording } from '../types/Recording';
import { IOrganisation, IMember, IGroup } from '../types/organisation';
import { IProject } from '../types/Project';
import { Location } from '../types/Location';
import { v4 as uuidv4 } from 'uuid';
import { Timestamp } from 'firebase/firestore';
import { generateAlbumKey } from '../utilities/albumKeyGenerator';

const DEMO_STORAGE_PREFIX = 'demo_';
const DEMO_PINS_KEY = `${DEMO_STORAGE_PREFIX}pins`;
const DEMO_ALBUMS_KEY = `${DEMO_STORAGE_PREFIX}albums`;
const DEMO_RECORDINGS_KEY = `${DEMO_STORAGE_PREFIX}recordings`;
const DEMO_ORG_PREFIX = `${DEMO_STORAGE_PREFIX}org_`;
const DEMO_ORG_KEY = `${DEMO_ORG_PREFIX}organisation`;
const DEMO_ORG_MEMBERS_KEY = `${DEMO_ORG_PREFIX}members`;
const DEMO_ORG_GROUPS_KEY = `${DEMO_ORG_PREFIX}groups`;
const DEMO_ORG_PROJECTS_KEY = `${DEMO_ORG_PREFIX}projects`;

// Helper functions
const getDemoData = <T>(key: string): Map<string, T> => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return new Map();
    const data = JSON.parse(stored);
    return new Map(Object.entries(data));
  } catch (error) {
    console.error(`Error reading demo data from ${key}:`, error);
    return new Map();
  }
};

const setDemoData = <T>(key: string, data: Map<string, T>): void => {
  try {
    const obj = Object.fromEntries(data);
    localStorage.setItem(key, JSON.stringify(obj));
  } catch (error) {
    console.error(`Error writing demo data to ${key}:`, error);
    throw error;
  }
};

const clearDemoData = (): void => {
  // Clear all demo-related localStorage items
  const keys = Object.keys(localStorage);
  keys.forEach(key => {
    if (key.startsWith(DEMO_STORAGE_PREFIX)) {
      localStorage.removeItem(key);
    }
  });
};

export const DemoDataManager = {
  // Pins
  getPins: (projectId: string): IPin[] => {
    const pins = getDemoData<IPin>(DEMO_PINS_KEY);
    const filteredPins = Array.from(pins.values()).filter(pin => pin.project === projectId);
    return filteredPins;
  },

  createPin: (pin: IPin): void => {
    const pins = getDemoData<IPin>(DEMO_PINS_KEY);
    const pinToSave = { ...pin, isDemo: true };
    pins.set(pin.pinKey, pinToSave);
    setDemoData(DEMO_PINS_KEY, pins);
  },

  updatePin: (pin: IPin): void => {
    const pins = getDemoData<IPin>(DEMO_PINS_KEY);
    if (pins.has(pin.pinKey)) {
      pins.set(pin.pinKey, pin);
      setDemoData(DEMO_PINS_KEY, pins);
    }
  },

  deletePin: (pinKey: string): void => {
    const pins = getDemoData<IPin>(DEMO_PINS_KEY);
    pins.delete(pinKey);
    setDemoData(DEMO_PINS_KEY, pins);
  },

  getPin: (pinKey: string): IPin | null => {
    const pins = getDemoData<IPin>(DEMO_PINS_KEY);
    return pins.get(pinKey) || null;
  },

  // Albums
  createAlbum: async (album: Album): Promise<Album> => {
    const albums = getDemoData<Album>(DEMO_ALBUMS_KEY);
    const albumKey = album.albumKey || await generateAlbumKey();
    const newAlbum = { ...album, albumKey };
    albums.set(albumKey, newAlbum);
    setDemoData(DEMO_ALBUMS_KEY, albums);
    return newAlbum;
  },

  getAlbum: (albumKey: string): Album | null => {
    const albums = getDemoData<Album>(DEMO_ALBUMS_KEY);
    return albums.get(albumKey) || null;
  },

  updateAlbum: (album: Album): void => {
    const albums = getDemoData<Album>(DEMO_ALBUMS_KEY);
    if (albums.has(album.albumKey!)) {
      albums.set(album.albumKey!, album);
      setDemoData(DEMO_ALBUMS_KEY, albums);
    }
  },

  deleteAlbum: (albumKey: string): void => {
    const albums = getDemoData<Album>(DEMO_ALBUMS_KEY);
    albums.delete(albumKey);
    setDemoData(DEMO_ALBUMS_KEY, albums);
  },

  // Recordings
  createRecording: (recording: IRecording): void => {
    const recordings = getDemoData<IRecording>(DEMO_RECORDINGS_KEY);
    recordings.set(recording.key, { ...recording, isDemo: true });
    setDemoData(DEMO_RECORDINGS_KEY, recordings);
  },

  getRecordings: (projectId: string): IRecording[] => {
    const recordings = getDemoData<IRecording>(DEMO_RECORDINGS_KEY);
    return Array.from(recordings.values()).filter(rec => rec.project === projectId);
  },

  getAllRecordings: (): IRecording[] => {
    const recordings = getDemoData<IRecording>(DEMO_RECORDINGS_KEY);
    return Array.from(recordings.values());
  },

  getRecordingsForPin: (pinKey: string): IRecording[] => {
    const recordings = getDemoData<IRecording>(DEMO_RECORDINGS_KEY);
    return Array.from(recordings.values()).filter(rec => rec.pinKey === pinKey);
  },

  updateRecording: (recordingKey: string, updates: Partial<IRecording>): void => {
    const recordings = getDemoData<IRecording>(DEMO_RECORDINGS_KEY);
    const recording = recordings.get(recordingKey);
    if (recording) {
      recordings.set(recordingKey, { ...recording, ...updates });
      setDemoData(DEMO_RECORDINGS_KEY, recordings);
    }
  },

  getRecording: (recordingKey: string): IRecording | null => {
    const recordings = getDemoData<IRecording>(DEMO_RECORDINGS_KEY);
    return recordings.get(recordingKey) || null;
  },

  getOrphanedRecordingsForProject: (projectKey: string): IRecording[] => {
    const recordings = getDemoData<IRecording>(DEMO_RECORDINGS_KEY);
    return Array.from(recordings.values()).filter(
      rec => rec.project === projectKey && (!rec.pinKey || rec.pinKey === '')
    );
  },

  deleteRecording: (recordingKey: string): void => {
    const recordings = getDemoData<IRecording>(DEMO_RECORDINGS_KEY);
    recordings.delete(recordingKey);
    setDemoData(DEMO_RECORDINGS_KEY, recordings);
  },

  // Clear all demo data (useful for testing or reset)
  clearAll: clearDemoData,

  // Check if demo data exists
  hasData: (): boolean => {
    const pins = getDemoData<IPin>(DEMO_PINS_KEY);
    const albums = getDemoData<Album>(DEMO_ALBUMS_KEY);
    const recordings = getDemoData<IRecording>(DEMO_RECORDINGS_KEY);
    return pins.size > 0 || albums.size > 0 || recordings.size > 0;
  },

  // Organisation demo data
  getDemoOrganisation: (): IOrganisation | null => {
    try {
      const stored = localStorage.getItem(DEMO_ORG_KEY);
      if (!stored) return null;
      return JSON.parse(stored) as IOrganisation;
    } catch (error) {
      console.error('Error reading demo organisation:', error);
      return null;
    }
  },

  getDemoMembers: (): IMember[] => {
    const members = getDemoData<IMember>(DEMO_ORG_MEMBERS_KEY);
    return Array.from(members.values());
  },

  getDemoGroups: (): IGroup[] => {
    const groups = getDemoData<IGroup>(DEMO_ORG_GROUPS_KEY);
    return Array.from(groups.values());
  },

  getDemoOrganisationProjects: (): IProject[] => {
    const projects = getDemoData<IProject>(DEMO_ORG_PROJECTS_KEY);
    return Array.from(projects.values());
  },

  updateDemoOrganisationProject: (project_id: string, updates: Partial<IProject>): void => {
    const projects = getDemoData<IProject>(DEMO_ORG_PROJECTS_KEY);
    const existing = projects.get(project_id);
    if (!existing) return;
    projects.set(project_id, { ...existing, ...updates });
    setDemoData(DEMO_ORG_PROJECTS_KEY, projects);
  },

  initializeOrganisationalDemoData: (): IOrganisation => {
    const demoOrgId = 'demo-org-001';
    const now = Timestamp.now();

    // Create demo organisation
    const demoOrganisation: IOrganisation = {
      id: demoOrgId,
      name: 'Demo School',
      type: 'school',
      description: 'A demonstration organisation for exploring closed organisational projects',
      adminId: 'demo-user',
      createdAt: now,
      updatedAt: now,
      isActive: true,
      settings: {
        allowAnonymousMembers: true,
        requireApproval: false,
      },
    };

    // Create demo members (mix of students and moderators)
    const demoMembers: IMember[] = [
      {
        userKey: 'demo-member-001',
        organisationId: demoOrgId,
        roles: ['member'],
        email: 'student1@demoschool.com',
        name: '🐶 Alex',
        joinedAt: now,
        groupIds: ['demo-group-001'],
        animalEmoji: '🐶',
        isTemporary: false,
      },
      {
        userKey: 'demo-member-002',
        organisationId: demoOrgId,
        roles: ['member'],
        email: 'student2@demoschool.com',
        name: '🐱 Sam',
        joinedAt: now,
        groupIds: ['demo-group-001'],
        animalEmoji: '🐱',
        isTemporary: false,
      },
      {
        userKey: 'demo-member-003',
        organisationId: demoOrgId,
        roles: ['member'],
        email: 'student3@demoschool.com',
        name: '🐭 Jordan',
        joinedAt: now,
        groupIds: ['demo-group-002'],
        animalEmoji: '🐭',
        isTemporary: false,
      },
      {
        userKey: 'demo-moderator-001',
        organisationId: demoOrgId,
        roles: ['moderator'],
        email: 'moderator1@demoschool.com',
        name: 'Ms. Johnson',
        joinedAt: now,
        groupIds: [],
        isTemporary: false,
      },
      {
        userKey: 'demo-moderator-002',
        organisationId: demoOrgId,
        roles: ['moderator'],
        email: 'moderator2@demoschool.com',
        name: 'Mr. Smith',
        joinedAt: now,
        groupIds: [],
        isTemporary: false,
      },
    ];

    // Create demo groups
    const demoGroups: IGroup[] = [
      {
        id: 'demo-group-001',
        name: 'Year 7',
        organisationId: demoOrgId,
        memberIds: ['demo-member-001', 'demo-member-002'],
        createdAt: now,
        updatedAt: now,
      },
      {
        id: 'demo-group-002',
        name: 'Year 8',
        organisationId: demoOrgId,
        memberIds: ['demo-member-003'],
        createdAt: now,
        updatedAt: now,
      },
    ];

    // Create demo closed projects
    const demoProjects: IProject[] = [
      {
        project_id: 'demo-project-org-001',
        projectName: 'Local History Project',
        shortDescription: 'Exploring our local area through audio stories',
        projectOwnerUserKey: 'demo-moderator-001',
        isPrivate: true,
        isAvailable: true,
        tags: {
          subjectTag: ['history', 'local'],
          contentTag: 'educational',
        },
        audioSize: 0,
        groupIds: ['demo-group-001', 'demo-group-002'],
      },
      {
        project_id: 'demo-project-org-002',
        projectName: 'Creative Writing Workshop',
        shortDescription: 'Students create audio narratives',
        projectOwnerUserKey: 'demo-moderator-002',
        isPrivate: true,
        isAvailable: true,
        tags: {
          subjectTag: ['creative', 'writing'],
          contentTag: 'workshop',
        },
        audioSize: 0,
        groupIds: ['demo-group-001'],
      },
    ];

    // Create demo pins for the demo projects
    const demoPins: IPin[] = [
      {
        pinKey: 'demo-pin-org-001',
        name: 'School Entrance',
        pinDescription: 'The main entrance to the school where our history project begins',
        location: {
          geoPoint: {
            lat: 52.481425,
            long: -1.917341
          }
        } as Location,
        project: 'demo-project-org-001',
        pinIcon: 'demo/OrgPin.png',
        pinType: 'location',
        isAvailable: true,
        isCollectionLogicEnabled: false,
        collectionLogic: null,
        pinOrder: null,
        branchParentPinKey: null,
        albumKey: null,
        isDemo: true,
        demoCreatedAt: now,
      },
      {
        pinKey: 'demo-pin-org-002',
        name: 'Local Library',
        pinDescription: 'Visit the local library to learn about the area\'s history',
        location: {
          geoPoint: {
            lat: 52.480835,
            long: -1.916314
          }
        } as Location,
        project: 'demo-project-org-001',
        pinIcon: 'demo/OrgPin.png',
        pinType: 'location',
        isAvailable: true,
        isCollectionLogicEnabled: false,
        collectionLogic: null,
        pinOrder: null,
        branchParentPinKey: null,
        albumKey: null,
        isDemo: true,
        demoCreatedAt: now,
      },
      {
        pinKey: 'demo-pin-org-003',
        name: 'Creative Writing Corner',
        pinDescription: 'A quiet space for students to work on their creative writing',
        location: {
          geoPoint: {
            lat: 52.481502,
            long: -1.916638
          }
        } as Location,
        project: 'demo-project-org-002',
        pinIcon: 'demo/OrgPin.png',
        pinType: 'location',
        isAvailable: true,
        isCollectionLogicEnabled: false,
        collectionLogic: null,
        pinOrder: null,
        branchParentPinKey: null,
        albumKey: null,
        isDemo: true,
        demoCreatedAt: now,
      },
    ];

    // Save to localStorage
    localStorage.setItem(DEMO_ORG_KEY, JSON.stringify(demoOrganisation));
    
    const membersMap = new Map<string, IMember>();
    demoMembers.forEach(member => membersMap.set(member.userKey, member));
    setDemoData(DEMO_ORG_MEMBERS_KEY, membersMap);

    const groupsMap = new Map<string, IGroup>();
    demoGroups.forEach(group => groupsMap.set(group.id, group));
    setDemoData(DEMO_ORG_GROUPS_KEY, groupsMap);

    const projectsMap = new Map<string, IProject>();
    demoProjects.forEach(project => projectsMap.set(project.project_id, project));
    setDemoData(DEMO_ORG_PROJECTS_KEY, projectsMap);

    // Save demo pins to the main demo pins storage
    const existingPins = getDemoData<IPin>(DEMO_PINS_KEY);
    demoPins.forEach(pin => {
      existingPins.set(pin.pinKey, pin);
    });
    setDemoData(DEMO_PINS_KEY, existingPins);

    return demoOrganisation;
  },

  clearOrganisationalDemoData: (): void => {
    localStorage.removeItem(DEMO_ORG_KEY);
    localStorage.removeItem(DEMO_ORG_MEMBERS_KEY);
    localStorage.removeItem(DEMO_ORG_GROUPS_KEY);
    localStorage.removeItem(DEMO_ORG_PROJECTS_KEY);
  },
};

